import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import { useState, useRef } from "react";
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { baseCrumbClasses } from "./Breadcrumbs";

interface ListSelectCrumbProps<T> {
  options: T[];
  selectedOption: T | null;
  setSelectedOption: (option: T | null) => void;
  getOptionValue: (option: T) => number;
  getOptionLabel: (option: T) => string;
  selectPlaceholder?: string;
  noOptionsPlaceholder?: string;
  getOptionClassName?: (option: T) => string;
  isLoading?: boolean;
  className?: string;
  renderSelected?: (option: T) => React.ReactNode;
}

export const ListSelectCrumb = <T,>(props: ListSelectCrumbProps<T>) => {
  const {
    options,
    selectedOption,
    setSelectedOption,
    getOptionValue,
    getOptionLabel,
    selectPlaceholder = "Choose an Option",
    noOptionsPlaceholder = "No Options Available",
    getOptionClassName,
    isLoading,
    className,
    renderSelected
  } = props;

  const anchorRef = useRef<HTMLButtonElement>(null);
  const [optionsVisible, setOptionsVisible] = useState(false);

  return (
    <>
      <button
        ref={anchorRef}
        className={`
				${baseCrumbClasses} ${className ?? ''} min-w-[4rem] short:max-w-[8rem] text-sm short:text-xs md:text-base p-2 short:py-1 short:px-4 lg:px-6 transition disabled:opacity-50 disabled:pointer-events-none
				${(options.length === 0) ?
            'font-normal italic opacity-75' :
            selectedOption == null ?
              'font-normal rounded-xl animate-pulse' :
              ''}`}
        onClick={() => setOptionsVisible(v => !v)}
        disabled={isLoading}
      >{isLoading ?
        <span className="animate-fade-in"><FontAwesomeIcon icon={faSpinner} spin /></span> :
        <span className="animate-fade-in">
          {options.length > 0 ?
            selectedOption != null ?
              renderSelected ? renderSelected(selectedOption) : getOptionLabel(selectedOption)
              : selectPlaceholder
            : noOptionsPlaceholder
          }
        </span>
        }
      </button>
      <Popper
        sx={{ zIndex: 20 }}
        open={optionsVisible}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={() => {
                setOptionsVisible(false);
              }}>
                <MenuList id="split-button-menu" autoFocusItem>
                  {options.length > 0 ? options.map((option) => (
                    <MenuItem
                      key={getOptionValue(option)}
                      className={`
                      ${option === selectedOption
                          ? '!bg-theme-primary !text-theme-primary-contrast'
                          : '!bg-transparent !text-inherit'}
                        ${getOptionClassName?.(option) ?? ''} !font-bold`}
                      selected={selectedOption != null && getOptionValue(option) === getOptionValue(selectedOption)}
                      onClick={() => {
                        setSelectedOption(option);
                        setOptionsVisible(false);
                      }}
                    >
                      {getOptionLabel(option)}
                    </MenuItem>
                  ))
                    : <MenuItem disabled className="!text-theme-text">{noOptionsPlaceholder}</MenuItem>
                  }
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}